import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  const userType = sessionStorage.getItem("userType");
  const location = useLocation();
  const currentUrl = window.location.href;
  const currentPath = location.pathname + location.search + location.hash;

  useEffect(() => {
    if (!loading && !user) {
      // Save both session and local storage for better persistence
      sessionStorage.setItem("redirectAfterLogin", currentPath);
      localStorage.setItem("redirectAfterLogin", currentPath);
      localStorage.setItem("fullRedirectUrl", currentUrl);

      // Add timestamp to know when this was saved
      localStorage.setItem("redirectTimestamp", Date.now().toString());
    } else if (!loading && user) {
      // Clear redirect data when user is logged in and not loading
      sessionStorage.removeItem("redirectAfterLogin");
      // Keep localStorage redirect for longer persistence but mark as used
      localStorage.setItem("redirectUsed", "true");
    }
  }, [user, loading, currentPath, currentUrl]);

  // Keep existing special case handling
  // Save subscription URL if applicable.
  if (currentUrl.includes("subscriptions?offerId=")) {
    localStorage.setItem("selectedPackageUrl", currentUrl);
  }
  // Save class details URL if the pathname includes "/classDetailsUser/"
  try {
    const urlObj = new URL(currentUrl);

    // Save all class detail URLs regardless of query params
    if (
      urlObj.pathname.includes("/classDetailsUser/") ||
      urlObj.pathname.includes("/newClassDetailsUser/")
    ) {
      localStorage.setItem("selectedClassUrl", currentUrl);
    }

    // Save group detail URL ONLY if user is not logged in and path is a group details page
    if (
      !loading &&
      !user &&
      (currentPath.includes("/groupDetailsTutor/") ||
        currentPath.includes("/groupDetailsUser/") ||
        currentPath.includes("/newGroupDetailsUser/"))
    ) {
      localStorage.setItem("selectedGroupUrl", currentUrl);
    }
  } catch (error) {
    console.error("Invalid URL", error);
  }

  // Show loading spinner while authentication is being determined
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-green-500"></div>
      </div>
    );
  }

  // If the user is not logged in after loading is complete, redirect to login page
  if (!user) {
    // Determine the login URL with appropriate query parameter
    let loginUrl = "/login";

    if (currentUrl.includes("subscriptions?offerId=")) {
      loginUrl = "/login?ref=sub";
    } else if (
      currentPath.includes("/classDetailsUser/") ||
      currentPath.includes("/newClassDetailsUser/")
    ) {
      loginUrl = "/login?ref=class";
    } else if (
      currentPath.includes("/groupDetailsTutor/") ||
      currentPath.includes("/groupDetailsUser/") ||
      currentPath.includes("/newGroupDetailsUser/")
    ) {
      loginUrl = "/login?ref=group";
    }

    return <Navigate to={loginUrl} />;
  }

  const forceProfileCompletion =
    user?.forceProfileCompletion ||
    sessionStorage.getItem("forceProfileCompletion") === "true";
  if (
    forceProfileCompletion &&
    location.pathname !== "/signup" &&
    location.pathname !== "/logout"
  ) {
    const resumeState = {
      resumeProfile: true,
      email: sessionStorage.getItem("forceProfileEmail") || user?.email || "",
    };
    return <Navigate to="/signup" state={resumeState} replace />;
  }

  if (location.pathname === "/login" || location.pathname === "/signup") {
    if (sessionStorage.getItem("signupInModal") === "true") {
      return children;
    }

    const hasCompleteProfile =
      user?.name && user?.email && user?.learningLanguage;
    const hasJoinedContent =
      (user?.joinedGroups && user.joinedGroups.length > 0) ||
      (user?.enrolledClasses && user.enrolledClasses.length > 0);

    if (hasCompleteProfile && hasJoinedContent) {
      return <Navigate to="/learn" />;
    } else {
      return <Navigate to="/profile-setup" />;
    }
  }

  // Role-based access checks
  const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

  // Only perform role checks if there are required roles
  if (roles.length > 0) {
    // Get user type from context first, then fallback to sessionStorage
    const userTypeFromContext = user?.userType;
    const effectiveUserType = userTypeFromContext || userType;

    // If user is authenticated but userType/profile is not yet loaded, show spinner
    if (!effectiveUserType || effectiveUserType === "undefined") {
      return (
        <div className="flex h-screen items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-green-500"></div>
        </div>
      );
    }

    // Only redirect to unauthorized if userType is loaded but not allowed
    if (!roles.includes(effectiveUserType)) {
      return <Navigate to="/unauthorized" />;
    }
  }

  return children;
};

export default ProtectedRoute;
